import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import WPLoader from "../../packages/WPLoader";

import { getLicensedDocumentationContent } from "./documentationApiHelpers";
import DocumentationContent from "./DocumentationContent";
import SideNav from "./SideNav";
import TableOfContents from "./TableOfContents";

import {
  INTRODUCTION_TAB,
  LICENSE_TAB_ID_TO_PERMISSION_IDS_MAP,
} from "../../constants/LicenseManagement.constants";

import {
  hasAssignedLicensePermission,
  hasEligibleLicensePermission,
} from "../../utils/LicenseUtils";

import "./LicensedDocumentation.scss";

const LicensedDocumentation = ({ userLicenses = [] }) => {
  const { t } = useTranslation();

  const userAssignedLicenseTabIds = useMemo(() => {
    return Object.entries(LICENSE_TAB_ID_TO_PERMISSION_IDS_MAP)
      .filter(([_, ids]) => hasAssignedLicensePermission(userLicenses, ids))
      .map(([licenseTabId]) => licenseTabId);
  }, [userLicenses]);

  const userAccessibleLicenseTabIds = useMemo(() => {
    return Object.entries(LICENSE_TAB_ID_TO_PERMISSION_IDS_MAP)
      .filter(([_, ids]) => hasEligibleLicensePermission(userLicenses, ids))
      .map(([licenseTabId]) => licenseTabId);
  }, [userLicenses]);

  const [selectedLicenseTabId, setSelectedLicenseTabId] = useState(
    INTRODUCTION_TAB.id,
  );
  const [isSideNavCollapsed, setIsSideNavCollapsed] = useState(false);
  const [docCacheByLicenseId, setDocCacheByLicenseId] = useState({});
  const [isContentLoading, setIsContentLoading] = useState(false);

  const isIntroductionTabSelected =
    selectedLicenseTabId === INTRODUCTION_TAB.id;
  const hasCachedDoc = Object.prototype.hasOwnProperty.call(
    docCacheByLicenseId,
    selectedLicenseTabId,
  );

  const cachedDocEntry = hasCachedDoc
    ? docCacheByLicenseId[selectedLicenseTabId]
    : null;

  const markdownContent = cachedDocEntry?.markdownContent || "";
  const contentHeadersList = cachedDocEntry?.headersList || [];

  useEffect(() => {
    let isCancelled = false;

    if (isIntroductionTabSelected || hasCachedDoc) {
      setIsContentLoading(false);
      return;
    }

    const loadDocumentation = async () => {
      setIsContentLoading(true);

      try {
        const { markdownWithResolvedImages, headersList } =
          await getLicensedDocumentationContent(selectedLicenseTabId);

        if (!isCancelled) {
          setDocCacheByLicenseId((previousCache) => ({
            ...previousCache,
            [selectedLicenseTabId]: {
              markdownContent: markdownWithResolvedImages,
              headersList,
            },
          }));
        }
      } catch {
        // Keep fallback rendering when markdown is unavailable.
      } finally {
        if (!isCancelled) {
          setIsContentLoading(false);
        }
      }
    };

    loadDocumentation();

    return () => {
      isCancelled = true;
    };
  }, [hasCachedDoc, isIntroductionTabSelected, selectedLicenseTabId]);

  // Scroll to top of the main container when the selected license changes.
  useEffect(() => {
    const scrollContainer = document.querySelector(".main-container");

    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0 });
    }
  }, [selectedLicenseTabId]);

  const shouldShowCombinedLoader =
    !isIntroductionTabSelected && isContentLoading;

  const handleHeaderClick = useCallback((headerId) => {
    const element = document.getElementById(headerId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  return (
    <div
      className={`licensed-documentation ${
        isSideNavCollapsed ? "licensed-documentation--sidenav-collapsed" : ""
      }`}
    >
      <SideNav
        userAccessibleLicenseTabIds={userAccessibleLicenseTabIds}
        selectedLicenseTabId={selectedLicenseTabId}
        setSelectedLicenseTabId={setSelectedLicenseTabId}
        isCollapsed={isSideNavCollapsed}
        setIsCollapsed={setIsSideNavCollapsed}
      />

      <div
        className="licensed-documentation__main-content-wrapper"
        style={shouldShowCombinedLoader ? { marginTop: "15px" } : {}}
      >
        {shouldShowCombinedLoader ? (
          <WPLoader isFocus={false} />
        ) : selectedLicenseTabId === INTRODUCTION_TAB.id || markdownContent ? (
          <>
            <DocumentationContent
              selectedLicenseTabId={selectedLicenseTabId}
              userAssignedLicenseTabIds={userAssignedLicenseTabIds}
              userAccessibleLicenseTabIds={userAccessibleLicenseTabIds}
              setSelectedLicenseTabId={setSelectedLicenseTabId}
              markdownContent={markdownContent}
            />
            {selectedLicenseTabId !== INTRODUCTION_TAB.id && (
              <TableOfContents
                headersList={contentHeadersList}
                onHeaderClick={handleHeaderClick}
              />
            )}
          </>
        ) : (
          <p>{t("licensedFeaturesDocumentation.docNotAvailable")}</p>
        )}
      </div>
    </div>
  );
};

export default LicensedDocumentation;
